import { useState, useEffect } from "react";
import { Bell, MessageSquare, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ClarificationResponse } from "./ClarificationResponse";
import { CorroborationPopup } from "./CorroborationPopup";

interface Notification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  is_anonymous: boolean;
  data: any;
  created_at: string;
}

export const NotificationBell = ({ workerId }: { workerId: string }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [clarificationDialog, setClarificationDialog] = useState<{
    isOpen: boolean;
    reportId: string;
    notificationId: string;
    message: string;
  }>({ isOpen: false, reportId: '', notificationId: '', message: '' });
  const [corroborationDialog, setCorroborationDialog] = useState<{
    isOpen: boolean;
    reportId: string;
    notificationId: string;
  }>({ isOpen: false, reportId: '', notificationId: '' });
  const { toast } = useToast();

  useEffect(() => {
    if (workerId) {
      fetchNotifications();
      
      // Set up realtime subscription
      const channel = supabase
        .channel('notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `recipient_id=eq.${workerId}`
          },
          (payload) => {
            console.log('New notification:', payload);
            fetchNotifications();
            
            // Show toast for new notification
            const newNotif = payload.new as Notification;
            toast({
              title: newNotif.title,
              description: newNotif.message,
            });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [workerId]);

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', workerId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching notifications:', error);
      return;
    }

    setNotifications(data || []);
    setUnreadCount(data?.filter(n => !n.is_read).length || 0);
  };

  const markAsRead = async (notificationId: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    fetchNotifications();
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Handle corroboration requests - open popup
    if (notification.notification_type === 'corroboration_request' && notification.data?.report_id) {
      setCorroborationDialog({
        isOpen: true,
        reportId: notification.data.report_id,
        notificationId: notification.id
      });
      setIsOpen(false);
    } else if (notification.data?.needs_response && notification.data?.report_id) {
      // This is a clarification request - open dialog
      setClarificationDialog({
        isOpen: true,
        reportId: notification.data.report_id,
        notificationId: notification.id,
        message: notification.message
      });
      setIsOpen(false);
    } else {
      await markAsRead(notification.id);
      setIsOpen(false);
    }
  };

  const needsResponseNotifications = notifications.filter(
    n => n.data?.needs_response && !n.is_read
  );

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Notifikasi</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                Tidak ada notifikasi
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`w-full text-left p-4 hover:bg-muted transition-colors border-b last:border-b-0 ${
                    !notif.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      {notif.notification_type === 'corroboration_request' && (
                        <Users className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                      {notif.data?.needs_response && notif.notification_type !== 'corroboration_request' && (
                        <MessageSquare className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      )}
                      <span className="font-medium text-sm">{notif.title}</span>
                    </div>
                    {!notif.is_read && (
                      <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {notif.message}
                  </p>
                  {notif.notification_type === 'corroboration_request' && !notif.is_read && (
                    <p className="text-xs text-primary mt-1 font-medium">
                      Klik untuk konfirmasi laporan
                    </p>
                  )}
                  {notif.data?.needs_response && notif.notification_type !== 'corroboration_request' && !notif.is_read && (
                    <p className="text-xs text-amber-600 mt-1 font-medium">
                      Klik untuk memberikan klarifikasi
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(notif.created_at).toLocaleString('id-ID')}
                  </p>
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ClarificationResponse
        isOpen={clarificationDialog.isOpen}
        onClose={() => {
          setClarificationDialog({ isOpen: false, reportId: '', notificationId: '', message: '' });
          fetchNotifications();
        }}
        reportId={clarificationDialog.reportId}
        notificationId={clarificationDialog.notificationId}
        originalMessage={clarificationDialog.message}
      />

      <CorroborationPopup
        isOpen={corroborationDialog.isOpen}
        onClose={() => {
          setCorroborationDialog({ isOpen: false, reportId: '', notificationId: '' });
          fetchNotifications();
        }}
        reportId={corroborationDialog.reportId}
        notificationId={corroborationDialog.notificationId}
      />
    </>
  );
};
